import { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { FaBell, FaTimes } from "react-icons/fa";
import '../styles/notification.css';
import { AuthContext } from "../context/AuthContext";

const NotificationBell = ({ onToggle }) => {
  const { notifications, setNotifications, fetchNotifications, markAsRead } = useContext(AuthContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const [visibleCount, setVisibleCount] = useState(10);
  const loaderRef = useRef(null);
  const [showPane, setShowPane] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const paneRef = useRef(null);

  useEffect(() => {
    const getNotifications = async () => {
      await fetchNotifications();
    };
    getNotifications();
  }, []);

  useEffect(() => {
    const count = notifications.filter(n => !n.isRead).length;
    setUnreadCount(count);
  }, [notifications]);


  const togglePane = () => {
    const newState = !showPane;
    setShowPane(newState);
    onToggle(newState);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (paneRef.current && !paneRef.current.contains(e.target)) {
        setShowPane(false);
        onToggle(false);
      }
    };

    if (showPane) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPane, onToggle]);

  useEffect(() => {
    if (!showPane) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount((prev) => prev + 10);
      }
    });

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [showPane]);

  return (
    <div className="notification-container">
      <div className={`bell-icon ${showPane ? 'active' : ''}`} onClick={togglePane}>
        <FaBell size={24} />
        {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
      </div>

      {showPane && <div className="notif-overlay"></div>}

      <div className={`notification-pane ${showPane ? 'open' : ''}`} ref={paneRef}>
        <div className="pane-header">
          <h2>Notifications</h2>
          <button className="notif-close-btn" onClick={togglePane}>
            <FaTimes />
          </button>
        </div>
        <div className="pane-body">
          {notifications.length === 0 ? (
            <div className="empty-center">
              <FaBell size={60} />
              <p>No notifications</p>
            </div>
          ) : (
            <>
              {notifications.slice(0, visibleCount).map(notification => {
                const title = notification.title || "Notification";
                const message = notification.message || "";
                const bodyPreview = message.length > 80 ? `${message.substring(0, 80)}...` : message;

                return (
                  <div
                    key={notification._id}
                    className={`notification-item ${notification.isRead ? "read" : "unread"}`}
                    onClick={() => {
                      markAsRead(notification._id);
                      setSelectedNotification(notification);
                      setShowPane(false);
                      onToggle(false);
                    }}
                  >
                    <small className="notif-time">
                      {new Intl.DateTimeFormat('en-NG', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                        timeZone: 'Africa/Lagos'
                      }).format(new Date(notification.createdAt))}

                    </small>
                    <p className="notif-title">{title}</p>
                    <p className="notif-body">{bodyPreview}</p>
                  </div>
                );
              })}
              {visibleCount < notifications.length && (
                <div ref={loaderRef} className="lazy-loader">Loading more...</div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedNotification && (
        <div className="notification-card-overlay">
          <div className="notification-card">

            {/* Top-right close for desktop */}
            <div className="close-desktop">
              <button className="dismiss-btn" onClick={() => setSelectedNotification(null)}>
                <FaTimes />
              </button>
            </div>

            <h3>{selectedNotification.title || "Notification"}</h3>
            <p className="notif-card-time">
              {new Intl.DateTimeFormat('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(selectedNotification.createdAt))}
            </p>
            <p className="notif-card-message">{selectedNotification.message}</p>

            {/* Bottom close for mobile */}
            <div className="close-mobile">
              <button className="dismiss-btn-mobile" onClick={() => setSelectedNotification(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default NotificationBell;
